/**
 * Удаление папки «Наших фото» (№438, спека 2026-10-29-dress-delete-design.md): сводка с сервера,
 * галочка «не предлагать модель в «Импорт топ»» у привязанной к CRM папки, причины отказа вместо кнопки.
 * После удаления — на `/photos`; кэш папки снимает вызывающий (`onDeleted`).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { btn, btnPrimary, ErrorLine, Loading } from "@/components/trends/ui";
import { deleteSummary } from "@/lib/dressDelete";
import { deleteDress, loadDressDelete } from "@/lib/photos";
import { Dialog } from "@/shared/ui/Dialog";

export function DeleteDressDialog({
  open,
  onClose,
  dressId,
  title,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  dressId: number;
  title: string;
  onDeleted: () => void;
}) {
  return (
    <Dialog open={open} title={`Удалить «${title}»?`} onClose={onClose}>
      {open ? <Body dressId={dressId} onClose={onClose} onDeleted={onDeleted} /> : null}
    </Dialog>
  );
}

function Body({ dressId, onClose, onDeleted }: { dressId: number; onClose: () => void; onDeleted: () => void }) {
  // Сводка свежая на каждое открытие: генерация или публикация могли появиться минуту назад.
  const preview = useQuery({
    queryKey: ["photos", "dress", dressId, "delete"],
    queryFn: () => loadDressDelete(dressId),
    gcTime: 0,
    staleTime: 0,
  });
  const [exclude, setExclude] = useState(true);
  const remove = useMutation({ mutationFn: () => deleteDress(dressId, exclude), onSuccess: onDeleted });

  if (preview.isPending) return <Loading />;
  if (preview.isError)
    return (
      <div className="p-4">
        <ErrorLine error={preview.error} />
      </div>
    );
  const p = preview.data;
  const blocked = p.blockers.length > 0;

  return (
    <div className="grid gap-3 p-4 text-[13px] text-fg" data-annot="удаление папки">
      {blocked ? (
        <div className="grid gap-1">
          <div className="font-medium">Удалить нельзя:</div>
          <ul className="list-disc pl-5 text-fg-muted">
            {p.blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          <ul className="grid gap-1 text-fg-muted">
            {deleteSummary(p).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {p.crm_model_id !== null ? (
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={exclude}
                onChange={(e) => setExclude(e.target.checked)}
                disabled={remove.isPending}
              />
              <span>
                Не предлагать модель в «Импорт топ»
                <span className="block text-[12px] text-fg-faint">вернуть — в «Импорт топ», блок «Исключённые модели»</span>
              </span>
            </label>
          ) : null}
        </>
      )}
      <ErrorLine error={remove.error} />
      <div className="flex justify-end gap-2">
        <button type="button" className={btn} onClick={onClose} disabled={remove.isPending}>
          {blocked ? "Закрыть" : "Отмена"}
        </button>
        {!blocked ? (
          <button
            type="button"
            className={btnPrimary}
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
          >
            <Trash2 size={13} />
            {remove.isPending ? "Удаляю…" : "Удалить папку"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
